import { UpdateSocketClient } from '../updates/UpdateSocketClient.js';

/**
 * NotificationManager handles document status notifications universally across the app
 * Integrates with the update stream and manages notification lifecycle
 */
export class NotificationManager {
  constructor() {
    // Notification storage - multiple types per documentId or taskId
    this.notifications = new Map(); // documentId/taskId -> { standard: [], error: [] }
    this.listeners = new Set(); // UI update listeners
    this.documentUpdateCallbacks = new Set(); // Document data update callbacks
    this.viewerUpdateCallbacks = new Set(); // Document viewer update callbacks
    this.taskUpdateCallbacks = new Set(); // Task data update callbacks
    this.taskViewerUpdateCallbacks = new Set(); // Task viewer update callbacks
    this.referralUpdateCallbacks = new Set(); // Referral data update callbacks
    this.consultationUpdateCallbacks = new Set(); // Consultation data update callbacks
    
    this.reconnectCallbacks = new Set(); // Catch-up callbacks run after a dropped connection
    this.connectionStateCallbacks = new Set(); // Notified on every transport state transition

    // Transport and cleanup
    this.streamInitialized = false;
    this.updateClient = null; // Owns the connection lifecycle
    this.cleanupTimer = null;
    
    // Configuration
    this.AUTO_HIDE_DELAY = 5000; // 5 seconds
    this.CLEANUP_INTERVAL = 1000; // Check every second
    
    // Notification limits per type per document
    this.NOTIFICATION_LIMITS = {
      standard: 1,
      error: 3
    };
    
    // Error state transitions (these indicate errors)
    this.ERROR_TRANSITIONS = new Set([
      'QUEUED_FOR_FILING->READY', // Failed to file away
      'PENDING->INVALID', // Document is invalid
      'GENERATING->INVALID', // Generation failed
    ]);
    
    // Start the cleanup timer
    this.startCleanupTimer();
  }

  /**
   * Get the current user's ID from session storage
   * Used to filter SSE notifications to only show toasts for the triggering user
   */
  getCurrentUserId() {
    try {
      const userData = JSON.parse(sessionStorage.getItem('user') || '{}');
      // Handle both snake_case and camelCase
      return userData.userId || null;
    } catch (error) {
      console.warn('Failed to get current user ID from session storage:', error);
      return null;
    }
  }

  /**
   * Format statusType enum values to display-friendly names
   * Matches the backend StatusType.toString() values
   */
  formatStatusType(statusType) {
    const statusTypeMap = {
      'SUMMARY': 'Summary',
      'BRIEF_SUMMARY': 'Brief summary',
      'NHS_ID': 'NHS number',
      'OWNING_PATIENT': 'Owning patient',
      'OUTCOME': 'Outcome tasks',
      'EXISTING_CODES': 'Existing codes',
      'NEW_CODES': 'New codes',
      'POTENTIAL_EXISTING_CODES': 'Potential existing codes',
      'DETAILS': 'Details',
      'MULTIPLE_PATIENTS': 'Multiple patients',
      'COMMENT': 'Comment',
      'ASSIGNEE': 'Assignee',
      'DOCUMENT_ROTATION': 'Document rotation',
      'MEASUREMENTS': 'Measurements',
      'RISK_CATEGORY': 'Urgency',
      'LMC_CONTRACT_VIOLATION': 'LMC contract violation',
      'LETTER_TYPE': 'Letter type',
      'TITLE': 'Title',
      'ALL': null // Generic update, use default message
    };
    return statusTypeMap[statusType] || null;
  }

  /**
   * Format referralStatusType enum values to display-friendly names
   */
  formatReferralStatusType(referralStatusType) {
    const referralStatusTypeMap = {
      'TEXT': 'Referral notes',
      'REFERRAL_TYPE': 'Referral type',
      'COMPLETE': 'Completion status',
      'VIEWER': null, // Handled separately
      'ALL': null // Generic update
    };
    return referralStatusTypeMap[referralStatusType] || null;
  }

  /**
   * Format taskStatusType enum values to display-friendly names
   * Matches the backend TaskStatusType.toString() values
   */
  formatTaskStatusType(taskStatusType) {
    const taskStatusTypeMap = {
      'TEXT': 'Task description',
      'ASSIGNEE': 'Assignee',
      'TASK_TYPE': 'Task type',
      'COMMENT': 'Comment',
      'COMPLETE': 'Completion status',
      'VIEWER': null, // Handled separately
      'ALL': null // Generic update, use default message
    };
    return taskStatusTypeMap[taskStatusType] || null;
  }

  /**
   * Format consultationStatusType enum values to display-friendly names
   * Matches the backend ConsultationStatusType values
   */
  formatConsultationStatusType(statusType) {
    const statusTypeMap = {
      'TRANSCRIPT': 'Transcript',
      'NOTES': 'Notes',
      'SUMMARY': 'Summary',
      'OWNING_PATIENT': 'Patient matching',
      'EXISTING_CODES': 'Existing codes',
      'NEW_CODES': 'New codes',
      'POTENTIAL_EXISTING_CODES': 'Potential existing codes',
      'MEASUREMENTS': 'Measurements',
      'OUTCOME': 'Outcome tasks',
      'NHS_ID': 'NHS number',
      'COMMENT': 'Comment',
      'ASSIGNEE': 'Assignee',
      'DETAILS': 'Details',
      'ALL': null // Generic update
    };
    return statusTypeMap[statusType] || null;
  }

  /**
   * Determine notification type based on options and status transition
   */
  getNotificationType(options, statusTransition) {
    const { isError = false } = options;
    
    // Check if it's an error
    const transitionKey = statusTransition ? `${statusTransition.from}->${statusTransition.to}` : null;
    const isActualError = isError || (transitionKey && this.ERROR_TRANSITIONS.has(transitionKey));
    
    if (isActualError) {
      return 'error';
    }
    
    // Otherwise it's standard
    return 'standard';
  }

  /**
   * Initialize notification storage for a document if it doesn't exist
   */
  initializeDocumentNotifications(documentId) {
    if (!this.notifications.has(documentId)) {
      this.notifications.set(documentId, {
        standard: [],
        error: []
      });
    }
  }

  /**
   * Add or update a notification for a document
   */
  addNotification(documentId, message, options = {}) {
    const now = Date.now();
    const {
      isError = false,
      statusTransition = null
    } = options;
    
    // Determine notification type
    const notificationType = this.getNotificationType(options, statusTransition);
    
    // Determine if this is an error based on status transition
    const transitionKey = statusTransition ? `${statusTransition.from}->${statusTransition.to}` : null;
    const isActualError = isError || (transitionKey && this.ERROR_TRANSITIONS.has(transitionKey));
    
    const notification = {
      documentId,
      message,
      isError: isActualError,
      timestamp: now,
      statusTransition,
      visible: true,
      type: notificationType
    };
    
    // Initialize document notifications if needed
    this.initializeDocumentNotifications(documentId);
    const documentNotifications = this.notifications.get(documentId);
    
    // Add notification based on type and limits
    const typeArray = documentNotifications[notificationType];
    const limit = this.NOTIFICATION_LIMITS[notificationType];
    
    // For standard notifications, replace the existing one
    if (notificationType === 'standard') {
      typeArray.length = 0; // Clear existing
      typeArray.push(notification);
    } else if (notificationType === 'error') {
      // For errors, add to the end and trim if over limit
      typeArray.push(notification);
      if (typeArray.length > limit) {
        typeArray.splice(0, typeArray.length - limit); // Keep only the latest N
      }
    }
    
    
    // Notify listeners
    this.notifyListeners();
  }

  /**
   * Remove a notification manually (removes all notifications for the document)
   */
  removeNotification(documentId) {
    if (this.notifications.has(documentId)) {
      this.notifications.delete(documentId);
      this.notifyListeners();
    }
  }

  /**
   * Remove a specific notification by type
   */
  removeNotificationByType(documentId, type) {
    if (this.notifications.has(documentId)) {
      const documentNotifications = this.notifications.get(documentId);
      documentNotifications[type] = [];
      
      // Remove the document entry if all types are empty
      const isEmpty = Object.values(documentNotifications).every(arr => arr.length === 0);
      if (isEmpty) {
        this.notifications.delete(documentId);
      }
      
      this.notifyListeners();
    }
  }

  /**
   * Get all visible notifications
   */
  getVisibleNotifications() {
    const allNotifications = [];
    
    for (const [documentId, documentNotifications] of this.notifications.entries()) {
      for (const [type, notifications] of Object.entries(documentNotifications)) {
        for (const notification of notifications) {
          if (notification.visible) {
            allNotifications.push(notification);
          }
        }
      }
    }
    
    // Sort by timestamp (newest first)
    return allNotifications.sort((a, b) => b.timestamp - a.timestamp);
  }

  /**
   * Start the update stream. Idempotent.
   * @param {ApiClient} apiClient - Authenticated API client instance
   */
  initializeUpdateStream(apiClient) {
    if (this.streamInitialized) {
      return;
    }

    if (!apiClient) {
      console.warn('[NotificationManager] apiClient not provided to initializeUpdateStream');
      return;
    }

    if (!this.updateClient) {
      this.updateClient = new UpdateSocketClient(apiClient);
    }

    this.updateClient.connect(
      this._handleUpdateMessage.bind(this),
      this._handleReconnect.bind(this),
      this._handleConnectionStateChange.bind(this)
    );
    this.streamInitialized = true;
  }

  /**
   * Force a fresh connection. Used on clinic change and when a hidden tab comes back to a
   * connection that has since died.
   * @param {ApiClient} apiClient - Authenticated API client instance
   */
  resetUpdateStream(apiClient) {
    this.notifications.clear();
    this.notifyListeners();

    if (!apiClient) {
      console.warn('[NotificationManager] apiClient not provided to resetUpdateStream');
      this.streamInitialized = false;
      return;
    }

    if (this.updateClient) {
      this.updateClient.reconnect(
        this._handleUpdateMessage.bind(this),
        this._handleReconnect.bind(this),
        this._handleConnectionStateChange.bind(this)
      );
      this.streamInitialized = true;
    } else {
      this.streamInitialized = false;
      this.initializeUpdateStream(apiClient);
    }
  }

  getConnectionState() {
    return this.updateClient ? this.updateClient.getConnectionState() : 'disconnected';
  }

  /** True when a handshake has been open longer than a handshake should ever take. */
  isHandshakeStalled() {
    return this.updateClient ? this.updateClient.isHandshakeStalled() : false;
  }

  disconnectUpdateStream() {
    if (this.updateClient) {
      this.updateClient.disconnect();
    }
    this.streamInitialized = false;
  }

  /**
   * Report that the user is reading an item, replacing the old periodic `/viewed` POST.
   * @param {{scope: string, itemId: string}|null} viewing
   */
  setViewing(viewing) {
    if (this.updateClient && typeof this.updateClient.setViewing === 'function') {
      this.updateClient.setViewing(viewing);
    }
  }

  /** Register a catch-up callback invoked after the connection is re-established. */
  addReconnectCallback(callback) {
    this.reconnectCallbacks.add(callback);
    return () => {
      this.reconnectCallbacks.delete(callback);
    };
  }

  _handleReconnect() {
    this.reconnectCallbacks.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        console.error('Error in reconnect callback:', error);
      }
    });
  }

  /** Register a callback invoked with the transport state on every transition. */
  addConnectionStateCallback(callback) {
    this.connectionStateCallbacks.add(callback);
    return () => {
      this.connectionStateCallbacks.delete(callback);
    };
  }

  _handleConnectionStateChange(state) {
    this.connectionStateCallbacks.forEach((callback) => {
      try {
        callback(state);
      } catch (error) {
        console.error('Error in connection state callback:', error);
      }
    });
  }

  /**
   * Shared message routing handler bound to this instance.
   * @private
   */
  _handleUpdateMessage(update) {
    if (update.updateType === 'document') {
      this.handleDocumentUpdate(update);
    } else if (update.updateType === 'task') {
      this.handleTaskUpdate(update);
    } else if (update.updateType === 'referral') {
      this.handleReferralUpdate(update);
    } else if (update.updateType === 'consultation') {
      this.handleConsultationUpdate(update);
    }
  }

  /**
   * Handle document status updates from SSE stream
   * 
   * IMPORTANT: This method handles TWO concerns that must remain separated:
   * 1. Data Synchronization (ALL users) - triggerDocumentUpdate() ensures every user's view stays current
   * 2. Toast Notifications (triggering user only) - addNotification() is filtered by userId
   */
  handleDocumentUpdate(update) {
    const { documentId, previousStatus, newStatus, statusType, progress, userId, message: statusMessage } = update.message;
    
    // Handle viewer updates separately
    if (statusType === 'VIEWER') {
      this.handleViewerUpdate(documentId, previousStatus, newStatus);
      return;
    }
    
    
    // Create status transition info
    const statusTransition = {
      from: previousStatus,
      to: newStatus,
      type: statusType,
      userId,
      progress,
    };
    
    // Check if this event was triggered by the current user
    // If userId is present and doesn't match, we still sync data but skip the notification toast
    const currentUserId = this.getCurrentUserId();
    const isOtherUser = userId && currentUserId && userId !== currentUserId;
    
    if (isOtherUser) {
      // ALWAYS trigger data sync for ALL users
      this.triggerDocumentUpdate(documentId, statusTransition);
      return; // Skip notification toast for other users' actions
    }
    
    // Skip toast if userId is null (system event)
    if (!userId) {
      this.triggerDocumentUpdate(documentId, statusTransition);
      return;
    }
    
    // Filter out noisy intermediate processing notifications
    // Only show notifications at key milestones: upload start and processing complete
    
    // Skip PENDING->GENERATING notifications (processing started)
    // User sees "Document uploaded" (NON_EXISTENT->PENDING) instead
    if (previousStatus === 'PENDING' && newStatus === 'GENERATING') {
      // Still trigger document update callbacks for data refresh
      this.triggerDocumentUpdate(documentId, statusTransition);
      return;
    }
    
    // Skip intermediate GENERATING->READY progress updates
    // Only show notification when document is fully complete
    if (previousStatus === 'GENERATING' && newStatus === 'READY') {
      // Only show notification for final completion (statusType === 'ALL')
      // Skip intermediate progress updates for individual components
      if (statusType !== 'ALL' && progress != null && progress < 1) {
        // Still trigger document update callbacks for data refresh
        this.triggerDocumentUpdate(documentId, statusTransition);
        return;
      }
    }
    
    // Determine message and options based on status transition
    let message = '';
    let options = {
      statusTransition,
      isError: false
    };
    
    // Map status transitions to user-friendly messages
    if (previousStatus === 'GENERATING' && newStatus === 'READY') {
      message = 'Document is ready';
    } else if (previousStatus === 'EDITING' && newStatus === 'READY') {
      // Use statusType for targeted editing messages
      const statusTypeDisplayName = this.formatStatusType(statusType);
      message = statusTypeDisplayName ? `${statusTypeDisplayName} updated` : 'Document updated';
    } else if (previousStatus === 'READY' && newStatus === 'NON_EXISTENT') {
      message = 'Document deleted';
    } else if (previousStatus === 'NON_EXISTENT' && newStatus === 'PENDING') {
      message = 'Document uploaded';
    } else if (previousStatus === 'PENDING' && newStatus === 'INVALID') {
      message = 'Document is invalid';
      options.isError = true;
    } else if (previousStatus === 'READY' && newStatus === 'PENDING') {
      message = 'Document queued for reprocessing';
    } else if (previousStatus === 'READY' && newStatus === 'QUEUED_FOR_FILING') {
      message = 'Document queued for filing';
    } else if (previousStatus === 'QUEUED_FOR_FILING' && newStatus === 'READY') {
      message = statusMessage
        ? `Failed to file document: ${statusMessage}`
        : 'Failed to file document';
      options.isError = true;
    } else if (previousStatus === 'QUEUED_FOR_FILING' && newStatus === 'FILED_AWAY') {
      message = 'Document filed successfully';
    } else if (previousStatus === 'READY' && newStatus === 'FILED_AWAY') {
      message = 'Document filed successfully';
    } else {
      // Fallback for unknown transitions
      message = `Status updated: ${newStatus}`;
    }
    
    // Add the notification (only shown to the triggering user at this point)
    this.addNotification(documentId, message, options);
    
    // Trigger document data update callbacks so views can refetch document data
    this.triggerDocumentUpdate(documentId, statusTransition);
  }

  /**
   * Handle task status updates from SSE stream
   * 
   * IMPORTANT: This method handles TWO concerns that must remain separated:
   * 1. Data Synchronization (ALL users) - triggerTaskUpdate() ensures every user's view stays current
   * 2. Toast Notifications (triggering user only) - addNotification() is filtered by userId
   */
  handleTaskUpdate(update) {
    const { taskId, previousStatus, newStatus, taskStatusType, userId } = update.message;
    
    
    // Handle viewer updates separately (silent sync, no toast)
    if (taskStatusType === 'VIEWER') {
      this.handleTaskViewerUpdate(taskId);
      return;
    }
    
    
    // Create status transition info
    const statusTransition = {
      from: previousStatus,
      to: newStatus,
      type: taskStatusType
    };
    
    // ALWAYS trigger data sync for ALL users
    this.triggerTaskUpdate(taskId, statusTransition);
    
    // Check if we should show toast notification
    // Skip toast if userId is null (system event) OR doesn't match current user
    const currentUserId = this.getCurrentUserId();
    const shouldShowToast = userId && currentUserId && userId === currentUserId;
    
    if (!shouldShowToast) {
      if (!userId) {
      } else {
      }
      return; // Skip notification toast
    }
    
    // Determine message and options based on status transition and type
    let message = '';
    let options = {
      statusTransition,
      isError: false
    };
    
    // Map status transitions to user-friendly messages
    if (previousStatus === 'EDITING' && newStatus === 'READY') {
      // Use taskStatusType for targeted editing messages
      const taskStatusTypeDisplayName = this.formatTaskStatusType(taskStatusType);
      message = taskStatusTypeDisplayName ? `${taskStatusTypeDisplayName} updated` : 'Task updated';
    } else if (previousStatus === 'READY' && newStatus === 'NON_EXISTENT') {
      message = 'Task deleted';
    } else if (newStatus === 'READY' && taskStatusType === 'COMPLETE') {
      message = 'Task marked complete';
    } else {
      // Fallback for unknown transitions
      message = `Task updated`;
    }
    
    // Add the notification (only shown to the triggering user at this point)
    this.addNotification(taskId, message, options);
  }

  /**
   * Handle referral status updates from SSE stream.
   *
   * Two concerns kept separate:
   * 1. Data Synchronization (ALL users) - triggerReferralUpdate() ensures every user's view stays current
   * 2. Toast Notifications (triggering user only) - filtered by userId
   */
  handleReferralUpdate(update) {
    const { referralId, previousStatus, newStatus, referralStatusType, userId } = update.message;

    // VIEWER updates are silent — no data refetch, no toast
    if (referralStatusType === 'VIEWER') {
      return;
    }

    const statusTransition = {
      from: previousStatus,
      to: newStatus,
      type: referralStatusType
    };

    // ALWAYS trigger data sync for ALL users
    this.triggerReferralUpdate(referralId, statusTransition);

    // Only show toast to the user who triggered the change
    const currentUserId = this.getCurrentUserId();
    const shouldShowToast = userId && currentUserId && userId === currentUserId;

    if (!shouldShowToast) {
      return;
    }

    let message = '';
    const options = { statusTransition, isError: false };

    if (previousStatus === 'EDITING' && newStatus === 'READY') {
      const displayName = this.formatReferralStatusType(referralStatusType);
      message = displayName ? `${displayName} updated` : 'Referral updated';
    } else if (newStatus === 'NON_EXISTENT') {
      message = 'Referral deleted';
    } else if (referralStatusType === 'COMPLETE' && newStatus === 'READY') {
      message = 'Referral marked complete';
    } else if (previousStatus === 'NON_EXISTENT') {
      message = 'Referral created';
    } else {
      message = 'Referral updated';
    }

    this.addNotification(referralId, message, options);
  }

  /**
   * Handle consultation status updates from SSE stream
   * 
   * IMPORTANT: This method handles TWO concerns that must remain separated:
   * 1. Data Synchronization (ALL users) - triggerConsultationUpdate() ensures every user's view stays current
   * 2. Toast Notifications (triggering user only) - addNotification() is filtered by userId
   */
  handleConsultationUpdate(update) {
    const { consultationId, previousStatus, newStatus, statusType, progress, userId, message: statusMessage } = update.message;
    
    // Create status transition info
    const statusTransition = {
      from: previousStatus,
      to: newStatus,
      type: statusType,
      progress: progress,
      userId
    };
    
    // Check if this event was triggered by the current user
    const currentUserId = this.getCurrentUserId();
    const isOtherUser = userId && currentUserId && userId !== currentUserId;
    
    if (isOtherUser) {
      // ALWAYS trigger data sync for ALL users
      this.triggerConsultationUpdate(consultationId, statusTransition);
      return; // Skip notification toast for other users' actions
    }
    
    // Skip toast if userId is null (system event) - but still trigger data sync
    if (!userId) {
      this.triggerConsultationUpdate(consultationId, statusTransition);
      return;
    }
    
    // Skip intermediate PENDING->GENERATING notifications
    if (previousStatus === 'PENDING' && newStatus === 'GENERATING') {
      this.triggerConsultationUpdate(consultationId, statusTransition);
      return;
    }
    
    // Skip intermediate GENERATING->READY progress updates
    if (previousStatus === 'GENERATING' && newStatus === 'READY') {
      if (statusType !== 'ALL' && progress != null && progress < 1) {
        this.triggerConsultationUpdate(consultationId, statusTransition);
        return;
      }
    }
    
    // Determine message and options based on status transition
    let message = '';
    let options = {
      statusTransition,
      isError: false
    };
    
    // Map status transitions to user-friendly messages
    if (previousStatus === 'NON_EXISTENT' && newStatus === 'RECORDING') {
      message = 'Consultation recording started';
    } else if (previousStatus === 'RECORDING' && newStatus === 'PENDING') {
      message = 'Recording completed, processing...';
    } else if (previousStatus === 'GENERATING' && newStatus === 'READY') {
      message = 'Consultation is ready';
    } else if (previousStatus === 'EDITING' && newStatus === 'READY') {
      const statusTypeDisplayName = this.formatConsultationStatusType(statusType);
      message = statusTypeDisplayName ? `${statusTypeDisplayName} updated` : 'Consultation updated';
    } else if (newStatus === 'NON_EXISTENT') {
      message = 'Consultation deleted';
    } else if (previousStatus === 'PENDING' && newStatus === 'INVALID') {
      message = 'Consultation processing failed';
      options.isError = true;
    } else if (previousStatus === 'READY' && newStatus === 'PENDING') {
      message = 'Consultation queued for reprocessing';
    } else if (previousStatus === 'READY' && newStatus === 'QUEUED_FOR_FILING') {
      message = 'Consultation queued for filing';
    } else if (previousStatus === 'QUEUED_FOR_FILING' && newStatus === 'READY') {
      message = statusMessage
        ? `Failed to file consultation: ${statusMessage}`
        : 'Failed to file consultation';
      options.isError = true;
    } else if (previousStatus === 'QUEUED_FOR_FILING' && newStatus === 'FILED_AWAY') {
      message = 'Consultation filed successfully';
    } else if (previousStatus === 'READY' && newStatus === 'FILED_AWAY') {
      message = 'Consultation filed successfully';
    } else {
      // Fallback for unknown transitions
      message = `Consultation status: ${newStatus}`;
    }
    
    // Add the notification (only shown to the triggering user at this point)
    this.addNotification(consultationId, message, options);
    
    // Trigger consultation data update callbacks so views can refetch data
    this.triggerConsultationUpdate(consultationId, statusTransition);
  }

  /**
   * Start cleanup timer to auto-hide old notifications
   */
  startCleanupTimer() {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }
    
    this.cleanupTimer = setInterval(() => {
      this.cleanupOldNotifications();
    }, this.CLEANUP_INTERVAL);
  }

  /**
   * Clean up old notifications based on type-specific rules
   * - Standard notifications: auto-hide after 5 seconds  
   * - Error notifications: persist (don't auto-hide)
   */
  cleanupOldNotifications() {
    const now = Date.now();
    let hasChanges = false;
    
    for (const [documentId, documentNotifications] of this.notifications.entries()) {
      // Process standard notifications for auto-hide
      const standardNotifications = documentNotifications.standard;
      
      for (let i = standardNotifications.length - 1; i >= 0; i--) {
        const notification = standardNotifications[i];
        const age = now - notification.timestamp;
        
        // Auto-hide standard notifications after 5 seconds
        if (age > this.AUTO_HIDE_DELAY && notification.visible) {
          notification.visible = false;
          hasChanges = true;
        }
        
        // Remove completely hidden notifications after 30 seconds to prevent memory leaks
        if (age > 30000) {
          standardNotifications.splice(i, 1);
          hasChanges = true;
        }
      }
      
      // Clean up empty document entries
      const allEmpty = Object.values(documentNotifications).every(arr => arr.length === 0);
      if (allEmpty) {
        this.notifications.delete(documentId);
        hasChanges = true;
      }
    }
    
    if (hasChanges) {
      this.notifyListeners();
    }
  }

  /**
   * Add a listener for notification updates
   */
  addListener(listener) {
    this.listeners.add(listener);
    
    // Return cleanup function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Add a callback for document data updates
   * Called when a document receives a status update and should have its data refreshed
   */
  addDocumentUpdateCallback(callback) {
    this.documentUpdateCallbacks.add(callback);
    
    // Return cleanup function
    return () => {
      this.documentUpdateCallbacks.delete(callback);
    };
  }

  /**
   * Add a callback for task data updates
   * Called when a task receives a status update and should have its data refreshed
   */
  addTaskUpdateCallback(callback) {
    this.taskUpdateCallbacks.add(callback);
    
    // Return cleanup function
    return () => {
      this.taskUpdateCallbacks.delete(callback);
    };
  }

  /**
   * Add a callback for referral data updates
   * Called when a referral receives a status update and should have its data refreshed
   */
  addReferralUpdateCallback(callback) {
    this.referralUpdateCallbacks.add(callback);

    return () => {
      this.referralUpdateCallbacks.delete(callback);
    };
  }

  /**
   * Add a callback for consultation data updates
   * Called when a consultation receives a status update and should have its data refreshed
   */
  addConsultationUpdateCallback(callback) {
    this.consultationUpdateCallbacks.add(callback);

    return () => {
      this.consultationUpdateCallbacks.delete(callback);
    };
  }

  /**
   * Handle viewer updates from SSE stream
   */
  handleViewerUpdate(documentId, previousStatus, newStatus) {
    
    // Trigger viewer update callbacks so components can refetch viewer data
    this.triggerViewerUpdate(documentId);
  }

  /**
   * Handle task viewer updates from SSE stream (silent sync only)
   */
  handleTaskViewerUpdate(taskId) {
    
    // Trigger task viewer update callbacks so components can refetch viewer data
    // No toast notification for viewer updates
    this.triggerTaskViewerUpdate(taskId);
  }

  /**
   * Add a callback for document viewer updates
   * Called when a document receives a viewer update and should have its viewer data refreshed
   */
  addViewerUpdateCallback(callback) {
    this.viewerUpdateCallbacks.add(callback);
    
    // Return cleanup function
    return () => {
      this.viewerUpdateCallbacks.delete(callback);
    };
  }

  /**
   * Add a callback for document viewer updates (alias for backwards compatibility)
   * Called when a document receives a viewer update and should have its viewer data refreshed
   */
  addDocumentViewerUpdateCallback(callback) {
    return this.addViewerUpdateCallback(callback);
  }

  /**
   * Add a callback for task viewer updates
   * Called when a task receives a viewer update and should have its viewer data refreshed
   */
  addTaskViewerUpdateCallback(callback) {
    this.taskViewerUpdateCallbacks.add(callback);
    
    // Return cleanup function
    return () => {
      this.taskViewerUpdateCallbacks.delete(callback);
    };
  }

  /**
   * Trigger all viewer update callbacks for a specific document
   */
  triggerViewerUpdate(documentId) {
    this.viewerUpdateCallbacks.forEach((callback, index) => {
      try {
        callback(documentId);
      } catch (error) {
        console.error('Error in viewer update callback:', error);
      }
    });
  }

  /**
   * Trigger all task viewer update callbacks for a specific task
   */
  triggerTaskViewerUpdate(taskId) {
    this.taskViewerUpdateCallbacks.forEach((callback, index) => {
      try {
        callback(taskId);
      } catch (error) {
        console.error('Error in task viewer update callback:', error);
      }
    });
  }

  /**
   * Trigger all document update callbacks for a specific document
   */
  triggerDocumentUpdate(documentId, statusTransition) {
    this.documentUpdateCallbacks.forEach(callback => {
      try {
        callback(documentId, statusTransition);
      } catch (error) {
        console.error('Error in document update callback:', error);
      }
    });
  }

  /**
   * Trigger all task update callbacks for a specific task
   */
  triggerTaskUpdate(taskId, statusTransition) {
    this.taskUpdateCallbacks.forEach(callback => {
      try {
        callback(taskId, statusTransition);
      } catch (error) {
        console.error('Error in task update callback:', error);
      }
    });
  }

  /**
   * Trigger all referral update callbacks
   */
  triggerReferralUpdate(referralId, statusTransition) {
    this.referralUpdateCallbacks.forEach(callback => {
      try {
        callback(referralId, statusTransition);
      } catch (error) {
        console.error('Error in referral update callback:', error);
      }
    });
  }

  /**
   * Trigger all consultation update callbacks
   */
  triggerConsultationUpdate(consultationId, statusTransition) {
    this.consultationUpdateCallbacks.forEach(callback => {
      try {
        callback(consultationId, statusTransition);
      } catch (error) {
        console.error('Error in consultation update callback:', error);
      }
    });
  }

  /**
   * Notify all listeners of notification changes
   */
  notifyListeners() {
    const visibleNotifications = this.getVisibleNotifications();
    this.listeners.forEach(listener => {
      try {
        listener(visibleNotifications);
      } catch (error) {
        console.error('Error in notification listener:', error);
      }
    });
  }

  /**
   * Clean up the notification manager
   */
  cleanup() {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }

    if (this.updateClient) {
      this.updateClient.disconnect();
      this.updateClient = null;
    }

    this.listeners.clear();
    this.documentUpdateCallbacks.clear();
    this.viewerUpdateCallbacks.clear();
    this.taskUpdateCallbacks.clear();
    this.taskViewerUpdateCallbacks.clear();
    this.referralUpdateCallbacks.clear();
    this.consultationUpdateCallbacks.clear();
    this.reconnectCallbacks.clear();
    this.connectionStateCallbacks.clear();
    this.notifications.clear();
    this.streamInitialized = false;
  }
}
